import type { DataTableColumn, TableColumnMeta } from "@/components/table";

import {
  BOOKING_COLUMN_IDS,
  BOOKING_COLUMN_LABELS,
  BOOKING_DEFAULT_VISIBLE_COLUMN_IDS,
  type BookingColumnId,
} from "./report/bookingLabels";
import type { MockBooking } from "@/types/booking";

type DataColumnId = BookingColumnId;

const DEFAULT_VISIBLE = new Set<string>(BOOKING_DEFAULT_VISIBLE_COLUMN_IDS);

/** Table header labels that differ from report/dump `BOOKING_COLUMN_LABELS`. */
const COLUMN_LABEL_OVERRIDES: Partial<Record<DataColumnId, string>> = {
  pricing_total: "Price",
};

/** Per-column layout overrides; labels come from `BOOKING_COLUMN_LABELS`. */
const COLUMN_LAYOUT: Partial<
  Record<DataColumnId | "actions", Omit<TableColumnMeta, "id" | "label">>
> = {
  source_id: { width: "168px", alwaysVisible: true },
  state: { width: "118px" },
  tags: { width: "140px" },
  carrier_contract: { width: "152px" },
  route: { width: "220px" },
  departure_date: {
    width: "96px",
    sortable: true,
    defaultSortDir: "asc",
  },
  arrival_date: {
    width: "96px",
    sortable: true,
    defaultSortDir: "asc",
  },
  effective_pcd: {
    width: "96px",
    defaultVisible: false,
    sortable: true,
    sortKey: "pcd",
    defaultSortDir: "asc",
  },
  cutoff_date: {
    width: "96px",
    defaultVisible: false,
    sortable: true,
    sortKey: "effective_cutoff",
    defaultSortDir: "asc",
  },
  stuffing_date: {
    width: "96px",
    defaultVisible: false,
    sortable: true,
    defaultSortDir: "asc",
  },
  ready_date: {
    width: "96px",
    defaultVisible: false,
    sortable: true,
    defaultSortDir: "asc",
  },
  open_date: {
    width: "96px",
    defaultVisible: false,
    sortable: true,
    defaultSortDir: "desc",
  },
  snapshot_date: {
    width: "104px",
    defaultVisible: false,
    sortable: true,
    defaultSortDir: "desc",
  },
  vessel: { width: "128px", defaultVisible: false },
  equipment: { width: "124px" },
  load_type: { width: "64px", defaultVisible: false },
  total_containers: { width: "64px", defaultVisible: false },
  total_teu: { width: "72px", defaultVisible: false },
  weight: { width: "88px", defaultVisible: false },
  shipper: { width: "128px" },
  consignee: { width: "128px", defaultVisible: false },
  agent: { width: "96px" },
  sales_person: { width: "120px", defaultVisible: false },
  opened_by: { width: "120px", defaultVisible: false },
  week: { width: "80px", defaultVisible: false },
  service_trade: { width: "128px", defaultVisible: false },
  commodity: { width: "128px", defaultVisible: false },
  pricing_total: { width: "84px" },
  rebook_price: { width: "100px", defaultVisible: false },
  best_saving: { width: "84px", defaultVisible: false },
  bl_group: { width: "120px", defaultVisible: false },
  bl_charge: { width: "88px", defaultVisible: false },
  delay_days: { width: "88px", defaultVisible: false },
  delay_original_etd: { width: "96px", defaultVisible: false },
  delay_new_etd: { width: "96px", defaultVisible: false },
  delay_source: { width: "120px", defaultVisible: false },
  delay_days_feeder: { width: "104px", defaultVisible: false },
  delay_original_etd_feeder: { width: "112px", defaultVisible: false },
  delay_new_etd_feeder: { width: "112px", defaultVisible: false },
  delay_days_mv: { width: "104px", defaultVisible: false },
  delay_original_etd_mv: { width: "112px", defaultVisible: false },
  delay_new_etd_mv: { width: "112px", defaultVisible: false },
  etd_delay_leg: { width: "120px", defaultVisible: false },
  eta_delay_days: { width: "88px", defaultVisible: false },
  delay_original_eta: { width: "96px", defaultVisible: false },
  delay_new_eta: { width: "96px", defaultVisible: false },
  eta_delay_source: { width: "120px", defaultVisible: false },
  dg_classes: { width: "100px", defaultVisible: false },
  rolled_from: { width: "128px", defaultVisible: false },
  exw_charge: { width: "96px", defaultVisible: false },
  ts_stops: { width: "72px", defaultVisible: false },
  actions: { width: "48px", alwaysVisible: true, pin: "last" },
};

function buildDataColumnMeta(id: DataColumnId): TableColumnMeta {
  const layout = COLUMN_LAYOUT[id] ?? { width: "96px" };
  return {
    id,
    label: COLUMN_LABEL_OVERRIDES[id] ?? BOOKING_COLUMN_LABELS[id],
    ...layout,
    defaultVisible: DEFAULT_VISIBLE.has(id)
      ? true
      : layout.defaultVisible,
  };
}

/** Table + report column catalog (data columns), aligned with booking-dump reports. */
export const BOOKING_TABLE_DATA_COLUMN_IDS: readonly DataColumnId[] =
  BOOKING_COLUMN_IDS;

export const BOOKING_TABLE_COLUMN_META: TableColumnMeta[] = [
  ...BOOKING_COLUMN_IDS.map(buildDataColumnMeta),
  {
    id: "actions",
    label: "Actions",
    ...COLUMN_LAYOUT.actions!,
  },
];

export function bookingColumnMeta(id: string): TableColumnMeta {
  const column = BOOKING_TABLE_COLUMN_META.find((item) => item.id === id);
  if (!column) {
    throw new Error(`Unknown booking table column: ${id}`);
  }
  return column;
}

type BookingColumnOverrides = Omit<
  DataTableColumn<MockBooking>,
  keyof TableColumnMeta
>;

/** Merge column meta with cell/render overrides (keeps `MockBooking` typing). */
export function bookingTableColumn(
  id: string,
  overrides: BookingColumnOverrides,
): DataTableColumn<MockBooking> {
  return { ...bookingColumnMeta(id), ...overrides };
}
